/*
 * Task Ribbon - container for an individual step that contains a task.
 * This is used in the TaskList component, and also for customers in the SubscriptionView component.
 *
 * This takes the following props:
 *   containerName    - handle declarative containment overrides
 *   auth             - authentication object
 *   task             - the task object.  For subscription tasks, this contains the subscription, which contains the app.
 *   done             - true if complete, false if not
 *   flatten          - true if in a flattened view, false if not
 *   completeAction   - method to execute on parent when the complete button is clicked
 *   undoAction       - method to execute on parent when the undo button is clicked
 *
 */
import React, { Component } from 'react';
import { Box, Button, Grid, Link, Paper, Typography } from '@mui/material';

import PropTypes from 'prop-types';

import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CheckCircleSharpIcon from '@mui/icons-material/CheckCircleSharp';
import TaskTypeAuth from './TaskTypeAuth';
import TaskTypeInviteLink from './TaskTypeInviteLink';
import TaskTypeCC from './TaskTypeCC';

// sx={{mb:2}}
class AppChiclet extends Component {
  render() {
    return (
      <Box sx={{mb:2}}>
        <Grid container spacing={1} alignContent="center" alignItems="center">
          <Grid item>
            <img src={this.props.logo} width={this.props.iconSize} height={this.props.iconSize} alt={this.props.name}/>
          </Grid>
          <Grid item>
            <Grid container direction="column" spacing={0}>
              <Grid item>
                <Typography variant="subtitle2">{this.props.name}</Typography>
              </Grid>
              {this.props.subtitle &&
              <Grid item>
                <Typography variant="body2">{this.props.subtitle}</Typography>
              </Grid>}
            </Grid>
          </Grid>
        </Grid>
      </Box>
    );
  }
}

    /*
      <Box sx={{mb:2}}> 
        <img src={this.props.logo} width={24} height={24} alt={this.props.name} style={{display:'inline', verticalAlign:'middle'}}/>
        <Typography variant="subtitle2" sx={{display:'inline', ml:1}}>{this.props.name}</Typography>
        {this.props.subtitle && 
        <Typography variant="body2">{this.props.subtitle}</Typography>}
      </Box>
        */


class Done extends Component {
  render() {
    return <Box sx={{verticalAlign:'center', textAlign:'right', paddingRight:2}}>
      <CheckCircleSharpIcon fontSize="small" color="success" sx={{verticalAlign:'middle', mr:1}}/> 
      <Typography variant="button">Complete</Typography>
      <br/>
      <Link size="small" onClick={this.props.onUndo}><Typography variant="body2">undo</Typography></Link>
    </Box>
  }
}

class TaskRibbon extends Component {
  constructor(props) {
    super(props);

    this.state = {
      completeDisabled: true,
    };
  }

  isCompleteDisabled = () => {
    return this.state.completeDisabled;
  }

  setCompleteDisabled = (cd) => {
    this.setState({completeDisabled: cd});
  }

  onDone = () => {
    if (this.props.completeTask) {
      Promise.resolve().then(() => {
        // console.log('Ribbon calling handleDoneFromParent');
        return this.child.handleDoneFromParent();
      }).then(() => {
        // console.log('Ribbon calling completeTask');
        this.props.completeTask(this.props.stepId);
      }).catch((error) => {
        console.log('TaskRibbon.onDone: error=' + JSON.stringify(error));
      });
    }
  }

  onUndo = () => {
    if (this.props.undoTask) {
      this.props.undoTask(this.props.stepId);
    }
  }

  render() {
    const completeButton = <Button onClick={this.onDone} variant="outlined" disabled={this.state.completeDisabled} startIcon={<CheckCircleOutlinedIcon/>}>Complete</Button>;

    let theTask = this.props.task;
    let theSub = null;
    let theApp = null;
    let appLogo = "https://cdn-icons-png.flaticon.com/512/62/62780.png";
    let appName = "Share your credit card";
    let iconSize = 24;
    // $Reseller.name requests a credit card to pay for subscriptions they sign up for on your behalf.

    /*
    let theResellerName = 'Your agent';
    if (this.props.requestor) {
      theResellerName = this.props.requestor;
    }
    let appSubtitle = theResellerName + " requests a credit card to pay for subscriptions they sign up for on your behalf.";
    */
    let appSubtitle = null;
 
    if (theTask.customerAppTByCustomerAppId != null) {
      theSub = theTask.customerAppTByCustomerAppId;
      theApp = theSub.appTByAppId;
      appLogo = theApp.appLogo;
      appName = theApp.appName;
      appSubtitle = null;
      iconSize = 24;
    }

    // Note that in the subscription view, we're showing the task in both pending and complete case for invite tasks, but only
    // in pending for auth tasks.
    if (this.props.containerName === 'SubscriptionView') {
      return (
        <Grid container spacing={1}>
          <Grid item xs style={{borderRight:"solid 1px #eee"}}>
            <Box sx={{paddingRight:2}}>
              {theTask.taskType === 0 &&
              <TaskTypeAuth
                title="Share your credentials"
                auth={this.props.auth}
                task={theTask}
                done={this.props.done}
                ref={(child) => this.child = child}
              />}
              {theTask.taskType === 1 &&
              <TaskTypeInviteLink
                title="Invite the team"
                auth={this.props.auth}
                task={theTask}
                done={this.props.done}
                ref={(child) => this.child = child}
              />}
            </Box >
          </Grid>
          <Grid item xs="auto">
            <Box sx={{p:2, boxSizing:'border-box', display:'flex', alignItems:'center', justifyContent:'right', height:"100%"}}>
              {this.props.done ? <Done onUndo={this.onUndo}/> : this.props.completeButton || completeButton}
            </Box>
          </Grid>
        </Grid>
      );
    }

    return (
      <Paper 
        elevation={this.props.flatten ? 0 : 1} 
        square={this.props.flatten}
        sx={this.props.flatten ? {borderBottom:'solid 1px #eee'} : {}}
      >
        <Grid container spacing={1}>
          <Grid item xs style={{borderRight:"solid 1px #eee"}}>
            <Box sx={{p:2}}>
              <AppChiclet
                logo={appLogo}
                name={appName}
                subtitle={appSubtitle}
                iconSize={iconSize}
              />
              {theTask.taskType === 0 &&
              <TaskTypeAuth
                auth={this.props.auth}
                order={this.props.stepOrder}
                requestor={this.props.requestor}
                task={theTask}
                done={this.props.done}
                isCompleteDisabled={this.isCompleteDisabled}
                setCompleteDisabled={this.setCompleteDisabled}
                ref={(child) => this.child = child}
              />}
              {theTask.taskType === 1 &&
              <TaskTypeInviteLink
                auth={this.props.auth}
                order={this.props.stepOrder}
                requestor={this.props.requestor}
                task={theTask}
                done={this.props.done}
                isCompleteDisabled={this.isCompleteDisabled}
                setCompleteDisabled={this.setCompleteDisabled}
                ref={(child) => this.child = child}
              />}
              {theTask.taskType === 3 &&
              <TaskTypeCC
                country="US"
                auth={this.props.auth}
                order={this.props.stepOrder}
                requestor={this.props.requestor}
                task={theTask}
                done={this.props.done}
                isCompleteDisabled={this.isCompleteDisabled}
                setCompleteDisabled={this.setCompleteDisabled}
                ref={(child) => this.child = child}
              />}
            </Box >
          </Grid>
          <Grid item xs="auto">
            <Box sx={{p:2, boxSizing:'border-box', display:'flex', alignItems:'center', justifyContent:'right', height:"100%"}}>
              {this.props.done ? <Done onUndo={this.onUndo}/> : this.props.completeButton || completeButton}
            </Box>
          </Grid>
        </Grid>
      </Paper>
    )
  }
}

TaskRibbon.propTypes = {
  auth: PropTypes.object.isRequired,
};


export default TaskRibbon;
